/**
 * Виды работ в профиле сотрудника — два подраздела.
 *
 * Основные работы приходят от должности и всегда остаются заблокированными.
 * Дополнительные работы редактируются общим MultiCombobox без отдельного режима:
 * optimistic chips показывают последнее намерение, а serial owner отправляет
 * replace-all PUT по одному и не теряет последний выбор во время запроса.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Lock } from "lucide-react";

import { showApiErrorToast } from "@/lib/api-error-toast";
import type { PersonWorkType } from "@/api/people";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { useAdminWorkTypes } from "@/hooks/useAdminWorkTypes";
import { useSetPersonWorkTypes } from "@/hooks/usePeople";
import {
  createSerialLatestIntentSet,
  type SerialLatestIntentSet,
  type SerialLatestIntentSnapshot,
} from "@/lib/serialLatestIntentSet";

interface Props {
  userId: number;
  /** Эффективный набор с именами: должность ∪ ad-hoc. */
  workTypes: PersonWorkType[];
  lockedIds: number[];
  adhocIds: number[];
  canEdit: boolean;
}

export function PersonWorkTypesSection({
  userId,
  workTypes,
  lockedIds,
  adhocIds,
  canEdit,
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const catalogQuery = useAdminWorkTypes(canEdit);
  const { mutateAsync: saveWorkTypes } = useSetPersonWorkTypes();
  const lockedIdSet = useMemo(() => new Set(lockedIds), [lockedIds]);
  const safeAdhocIds = useMemo(
    () => adhocIds.filter((id) => !lockedIdSet.has(id)),
    [adhocIds, lockedIdSet],
  );
  const [projection, setProjection] = useState<SerialLatestIntentSnapshot>(() => ({
    confirmedIds: [...safeAdhocIds],
    intendedIds: [...safeAdhocIds],
    isPending: false,
  }));
  const [owner] = useState<SerialLatestIntentSet>(() =>
    createSerialLatestIntentSet({
      initialIds: safeAdhocIds,
      save: (ids) => saveWorkTypes({ userId, ids }),
      onProjectionChange: setProjection,
      onError: (error) => showSaveError(error, t("common.apiError.fallback")),
    }),
  );

  useEffect(() => {
    owner.updateCallbacks({
      save: (ids) => saveWorkTypes({ userId, ids }),
      onError: (error) => showSaveError(error, t("common.apiError.fallback")),
    });
  }, [owner, saveWorkTypes, t, userId]);

  useEffect(() => {
    owner.syncConfirmedIds(safeAdhocIds);
  }, [owner, safeAdhocIds]);

  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const byId = useMemo(() => {
    const entries: Array<[number, PersonWorkType]> = workTypes.map((workType) => [
      workType.id,
      workType,
    ]);
    for (const workType of catalog) {
      if (workType.id == null) continue;
      entries.push([
        workType.id,
        { id: workType.id, name: workType.name, is_active: workType.is_active },
      ]);
    }
    return new Map(entries);
  }, [catalog, workTypes]);
  const locked = resolve(lockedIds, byId);
  const adhoc = resolve(projection.intendedIds, byId);
  const options = catalog.flatMap((workType) => {
    if (workType.id == null) return [];
    if (lockedIdSet.has(workType.id)) return [];
    if (!workType.is_active && !projection.intendedIds.includes(workType.id)) return [];
    return [{ id: workType.id, label: workType.name }];
  });
  const pickerDisabled = catalogQuery.isLoading || catalogQuery.isError;

  return (
    <>
      {locked.length > 0 && (
        <div className="mt-6">
          <div className="mb-4">
            <h3 className="text-sm font-semibold">{t("people.workTypesTitle")}</h3>
          </div>
          <ul className="flex flex-wrap gap-2" data-testid="person-work-types">
            {locked.map((workType) => (
              <WorkTypeChip
                key={workType.id}
                workType={workType}
                locked
                testId="person-work-type"
              />
            ))}
          </ul>
          <span className="text-muted-foreground mt-2 block text-xs">
            {t("people.workTypesLockedHint")}
          </span>
        </div>
      )}

      {(adhoc.length > 0 || canEdit) && (
        <div className="mt-6" data-testid="person-work-types-adhoc-section">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold">{t("people.workTypesAdhocTitle")}</h3>
            {canEdit && (
              <div className="w-full sm:w-72" data-pending={projection.isPending || undefined}>
                <MultiCombobox
                  data-testid="person-work-types-picker"
                  options={options}
                  selectedIds={projection.intendedIds}
                  onSelectedIdsChange={(ids) =>
                    owner.setIntendedIds(ids.filter((id) => !lockedIdSet.has(id)))
                  }
                  placeholder={t("people.workTypesPicker")}
                  searchPlaceholder={t("people.workTypesSearch")}
                  emptyText={t("common.combobox.empty")}
                  selectAllLabel={t("common.multiSelect.selectAll")}
                  clearAllLabel={t("common.multiSelect.clearAll")}
                  selectedLabel={t("common.multiSelect.selected")}
                  disabled={pickerDisabled}
                />
              </div>
            )}
          </div>

          {catalogQuery.isError && canEdit ? (
            <ApiErrorNotice
              error={catalogQuery.error}
              fallbackMessage={t("common.apiError.fallback")}
              className="text-destructive text-sm"
            />
          ) : adhoc.length === 0 ? (
            <span
              className="text-muted-foreground block text-sm"
              data-testid="person-work-types-adhoc-empty"
            >
              {t("people.workTypesAdhocEmpty")}
            </span>
          ) : (
            <ul className="flex flex-wrap gap-2" data-testid="person-work-types-adhoc">
              {adhoc.map((workType) => (
                <WorkTypeChip
                  key={workType.id}
                  workType={workType}
                  testId="person-work-type-adhoc"
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

function WorkTypeChip({
  workType,
  locked = false,
  testId,
}: {
  workType: PersonWorkType;
  locked?: boolean;
  testId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <li
      className="flex items-center gap-2 rounded-[var(--radius-chip)] border px-2.5 py-1.5 text-sm"
      style={{ borderColor: "var(--rule-line)" }}
      data-testid={testId}
    >
      <span
        aria-hidden="true"
        className="flex size-4 items-center justify-center rounded-full bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)]"
      >
        {locked ? (
          <Lock className="size-2.5" strokeWidth={2.5} />
        ) : (
          <Check className="size-3" strokeWidth={3} />
        )}
      </span>
      <span className={workType.is_active ? undefined : "text-muted-foreground"}>
        {workType.name}
        {!workType.is_active && (
          <span className="text-muted-foreground text-xs">{t("people.workTypesInactive")}</span>
        )}
      </span>
    </li>
  );
}

function resolve(ids: readonly number[], byId: Map<number, PersonWorkType>): PersonWorkType[] {
  return ids
    .map((id) => byId.get(id))
    .filter((workType): workType is PersonWorkType => workType != null);
}

function showSaveError(error: unknown, fallback: string): void {
  showApiErrorToast(error, { fallbackMessage: fallback });
}
